import type {
  ChecklistEntry,
  ChecklistInternalCase,
  ChecklistResponse,
  ChecklistSection,
  ChecklistSlot
} from "@/api/types/foreignNationalDocument";

const DAY_MS = 86_400_000;

/** `YYYY-MM-DD` (or an ISO timestamp) as a local date. */
export const parseDay = (iso: string): Date => {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

export const daysBetween = (from: Date, to: Date): number =>
  Math.round((to.getTime() - from.getTime()) / DAY_MS);

const fmtLong = (d: Date) =>
  d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });

/** "Aug 2" this year, "Feb 2023" otherwise. */
export const fmtShort = (d: Date, today: Date): string =>
  d.getFullYear() === today.getFullYear()
    ? d.toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : d.toLocaleDateString("en-US", { month: "short", year: "numeric" });

const ago = (iso: string, today: Date): string => {
  const d = parseDay(iso);
  const n = daysBetween(d, today);
  if (n === 0) return "today";
  if (n === 1) return "yesterday";
  if (n > 1 && n < 7) return `${n} days ago`;
  return fmtShort(d, today);
};

/** "Expires Mar 3, 2031", "Ends Jan 15, 2027 · 92 days", "Expired Jan 9, 2026", "No expiry". */
export const expiryText = (entry: ChecklistEntry, today: Date): string => {
  if (!entry.expires) return "No expiry";
  const end = parseDay(entry.expires);
  if (entry.expiry_tone === "expired") return `Expired ${fmtLong(end)}`;
  if (entry.expiry_tone === "soon")
    return `Ends ${fmtLong(end)} · ${daysBetween(today, end)} days`;
  return `Expires ${fmtLong(end)}`;
};

export const expiryClass = (entry: ChecklistEntry): string =>
  !entry.expires
    ? "text-fg-4"
    : entry.expiry_tone === "expired"
    ? "text-danger"
    : entry.expiry_tone === "soon"
    ? "text-warn"
    : "text-fg-3";

/** "Uploaded by Priya · Aug 2", "Added by Dana Whitfield · Feb 2023", "Added to H-1B 2023 · Feb 2023". */
export const byline = (
  entry: ChecklistEntry,
  today: Date,
  clientName: string | null
): string => {
  const by = entry.added_by;
  const who =
    by.kind === "client"
      ? `Uploaded by ${by.name || clientName || "the client"}`
      : by.kind === "firm"
      ? by.name
        ? `Added by ${by.name}`
        : "Added by your team"
      : `Added to ${by.case_label || "a case"}`;
  return entry.added_at ? `${who} · ${ago(entry.added_at, today)}` : who;
};

export const entryTitle = (entry: ChecklistEntry, slot?: ChecklistSlot) =>
  entry.display_name ||
  slot?.name ||
  entry.filename ||
  entry.title ||
  "Document";

export const isPdfEntry = (entry: ChecklistEntry): boolean =>
  /pdf/i.test(entry.mime_type ?? "") || /\.pdf$/i.test(entry.filename ?? "");

/** The case tags on a row: one per case. */
export const caseTags = (entry: ChecklistEntry): string[] =>
  Array.from(new Set(entry.cases.map((c) => c.case_label).filter(Boolean)));

const haystack = (entry: ChecklistEntry, slot?: ChecklistSlot) =>
  [
    entry.display_name,
    slot?.name,
    entry.filename,
    entry.title,
    entry.summary,
    ...caseTags(entry)
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

export type ChecklistFilter = { attention: boolean; query: string };

/** A slot with a document on file; expected types with nothing uploaded aren't listed. */
const onFile = (slot: ChecklistSlot) => slot.entries.length > 0;

/** The sections and slots a filter keeps; a slot shows when it or any of its documents match. */
export const filterSections = (
  data: ChecklistResponse,
  { attention, query }: ChecklistFilter
): ChecklistSection[] => {
  const q = query.trim().toLowerCase();
  return data.sections
    .map((section) => ({
      ...section,
      slots: section.slots.filter((slot) => {
        if (!onFile(slot)) return false;
        if (attention && !slot.attention) return false;
        if (!q) return true;
        if (slot.name.toLowerCase().includes(q)) return true;
        return [...slot.entries, ...slot.older].some((e) =>
          haystack(e, slot).includes(q)
        );
      })
    }))
    .filter((section) => section.slots.length > 0);
};

export const filterOther = (
  data: ChecklistResponse,
  { attention, query }: ChecklistFilter
): ChecklistEntry[] => {
  const q = query.trim().toLowerCase();
  return data.other.filter(
    (e) => (!attention || Boolean(e.review)) && (!q || haystack(e).includes(q))
  );
};

/** The Internal folder's cases a filter keeps; nothing in it needs attention. */
export const filterInternal = (
  data: ChecklistResponse,
  { attention, query }: ChecklistFilter
): ChecklistInternalCase[] => {
  if (attention) return [];
  const q = query.trim().toLowerCase();
  return (data.internal ?? [])
    .map((c) => ({
      ...c,
      items: c.items.filter(
        (i) =>
          !q ||
          [i.title, i.subtitle, c.case_label]
            .join(" ")
            .toLowerCase()
            .includes(q)
      )
    }))
    .filter((c) => c.items.length > 0);
};

/** The Needs Attention count over the rows shown (the server's also counts missing types). */
export const attentionCount = (data: ChecklistResponse): number =>
  data.sections.reduce(
    (n, s) =>
      n + s.slots.filter((slot) => onFile(slot) && slot.attention).length,
    0
  ) + data.other.filter((e) => Boolean(e.review)).length;

/** The documents a section lists: every current copy across its slots. */
export const sectionCount = (section: ChecklistSection): number =>
  section.slots.reduce((n, slot) => n + slot.entries.length, 0);

/** Every selectable document (current rows and Other; older versions aren't). */
export const selectableEntries = (
  data: ChecklistResponse
): ChecklistEntry[] => [
  ...data.sections.flatMap((s) => s.slots.flatMap((slot) => slot.entries)),
  ...data.other
];
